import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/content';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';
import { ArticleSchema } from '@/components/base/SchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <div className="max-w-lg mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-2xl text-accent-500"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-900 mb-3">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const url = `https://haijiao.readdy.ai/article/${slug}`;

  return (
    <>
      <PageSEO
        title={`${article.title} - 海角最新地址`}
        description={article.excerpt}
        keywords={`海角最新地址,海角地址,${article.title}`}
        canonicalUrl={url}
        ogType="article"
      >
        <ArticleSchema
          title={article.title}
          description={article.excerpt}
          author={article.author}
          datePublished={article.publishedAt}
          url={url}
        />
      </PageSEO>

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: category?.name || '文章', path: category ? `/category/${category.slug}` : undefined },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full mb-4 hover:bg-accent-200 transition-colors"
              >
                {category.name}
              </Link>
            )}
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 leading-snug">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
            </div>
          </div>

          <div className="aspect-[21/9] rounded-xl overflow-hidden mb-8">
            <img
              src={article.heroImageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800
              prose-h2:font-heading prose-h2:text-xl prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4
              prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-4
              prose-li:text-foreground-700 prose-li:mb-1
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors"
              >
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-400">分享文章：</span>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                    } catch { /* fallback */ }
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-foreground-600 hover:bg-secondary-200 transition-colors cursor-pointer"
                  title="复制链接"
                >
                  <i className="ri-link text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          {(() => {
            const related = articles.filter((a) => a.slug !== slug && a.category === article.category).slice(0, 3);
            if (related.length === 0) return null;
            return (
              <div className="mt-12">
                <h2 className="font-heading text-xl text-foreground-900 mb-5">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {related.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 p-4 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <h3 className="font-heading text-sm text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{ra.publishedAt} · {ra.readTime}</p>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </article>
    </>
  );
}